/* assets/partners-map.js */
class PartnersMap extends HTMLElement {
  constructor() {
    super();
    this.apiKey = this.dataset.apiKey;
    this.initialZoom = parseInt(this.dataset.zoom) || 6;
    this.markerColor = this.dataset.markerColor || '#01df82';
    
    this.map = null;
    this.markers = [];
    this.partners = [];

    this.dataSource = this.dataset.dataSource || 'metaobjects';
    this.jsonUrl = this.dataset.jsonUrl;
    this.forcedCategory = this.dataset.forcedCategory || 'all';
    this.locale = this.dataset.locale || document.documentElement.lang || 'pl';

    this.state = {
      category: this.forcedCategory,
      country: 'all',
      keyword: '',
      boundsFilter: null // Used for "Search this area"
    };
    this.enableGeolocation = this.dataset.enableGeolocation === 'true';
    this.userLocation = null;

    this.elements = {
      canvas: this.querySelector('[data-map-canvas]'),
      radioCards: Array.from(this.querySelectorAll('.partners-map__radio-card')),
      countrySelect: this.querySelector('[data-country-select]'),
      resetCountryBtn: this.querySelector('[data-reset-country]'),
      searchInput: this.querySelector('[data-search-input]'),
      resetButton: this.querySelector('[data-reset-search]'),
      partnerList: this.querySelector('[data-partner-list]'),
      resultsCount: this.querySelector('[data-results-count]'),
      noResults: this.querySelector('[data-no-results]'),
      content: this.querySelector('.partners-map__content'),
      mobileTabButtons: Array.from(this.querySelectorAll('[data-tab]')),
      mobilePopup: this.querySelector('[data-mobile-popup]'),
      mobilePopupClose: this.querySelector('[data-mobile-popup-close]'),
      mobilePopupBody: this.querySelector('[data-mobile-popup-body]'),
      partnerCards: []
    };

    this.createSearchAreaButton();

    if (!this.apiKey) {
      console.error('Partner Map: API Key is missing.');
      return;
    }

    this.activeMarkerIndex = null;
    this.iconCache = new Map();
    this.init();
  }

  createSearchAreaButton() {
    this.searchAreaBtn = document.createElement('button');
    this.searchAreaBtn.className = 'partners-map__search-area-btn';
    this.searchAreaBtn.innerText = 'Szukaj na tym obszarze';
    this.elements.canvas.parentElement.appendChild(this.searchAreaBtn);
  }

  async init() {
    await this.loadGoogleMaps();
    this.initMap();
    
    if (this.dataSource === 'json' && this.jsonUrl) {
      await this.loadPartnersFromJson();
    } else {
      await this.loadPartnersFromMetaobjects();
    }

    this.initCountryDropdown();
    this.addEventListeners();
    this.renderMarkers();
    this.filterAndSortPartners();
    this.initStickyObserver();
    
    // Initial fit bounds removed as per user request to keep default global center
  }

  initStickyObserver() {
    const mapSection = this;
    const scrollSpy = document.querySelector('.scrollspy-section');
    
    if (mapSection && scrollSpy) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            scrollSpy.classList.add('is-hidden-by-map');
          } else {
            scrollSpy.classList.remove('is-hidden-by-map');
          }
        });
      }, {
        threshold: 0,
        rootMargin: '-210px 0px -90% 0px' 
      });
      
      observer.observe(mapSection);
    }
  }

  async loadPartnersFromJson() {
    try {
      const response = await fetch(this.jsonUrl);
      const data = await response.json();
      this.renderPartnersFromJson(data);
    } catch (error) {
      console.error('Partner Map: Error loading JSON data:', error);
    }
  }

  async loadPartnersFromMetaobjects() {
    const token = this.dataset.storefrontToken;
    const domain = this.dataset.shopDomain;

    if (token && domain) {
      await this.loadPartnersFromStorefront(domain, token);
      return;
    }

    // Fallback: Liquid-rendered JSON (max 250 via paginate)
    const scriptTag = this.querySelector('script[data-partners-json]');
    if (!scriptTag) {
      this.initPartnersFromDom();
      return;
    }
    try {
      this.renderPartnersFromJson(JSON.parse(scriptTag.textContent));
    } catch (e) {
      console.error('Partner Map: Error parsing Metaobjects JSON:', e);
      this.initPartnersFromDom();
    }
  }

  async loadPartnersFromStorefront(domain, token) {
    const endpoint = `https://${domain}/api/2024-01/graphql.json`;
    const query = `
      query GetPartners($first: Int!, $after: String) {
        metaobjects(type: "point", first: $first, after: $after) {
          edges {
            node {
              fields { key value }
            }
          }
          pageInfo { hasNextPage endCursor }
        }
      }
    `;

    const allPartners = [];
    let cursor = null;
    let hasNextPage = true;

    while (hasNextPage) {
      try {
        const resp = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token
          },
          body: JSON.stringify({ query, variables: { first: 250, after: cursor } })
        });
        const { data } = await resp.json();
        const { edges, pageInfo } = data.metaobjects;

        edges.forEach(({ node }) => {
          const f = {};
          node.fields.forEach(({ key, value }) => { f[key] = value; });
          allPartners.push({
            name: f.name || f.title || f.label,
            category: f.point_type,
            address: f.address || f.adress,
            city: f.city,
            post_code: f.post_code || f.postal_code,
            country: f.country,
            phone: f.phone,
            website: f.website,
            lat: f.position_lat || f.latitude || f.lat,
            lng: f.position_lon || f.longitude || f.lng,
            priority: f.priority || null
          });
        });

        hasNextPage = pageInfo.hasNextPage;
        cursor = pageInfo.endCursor;
      } catch (err) {
        console.error('Partner Map: Storefront API error:', err);
        hasNextPage = false;
      }
    }

    this.renderPartnersFromJson(allPartners);
  }

  // Normalizacja nazwy kraju do porównań: małe litery, bez diakrytyków, pojedyncze spacje.
  normalizeCountry(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // zdejmij znaki diakrytyczne
      .replace(/\s+/g, ' ')
      .trim();
  }

  // Buduje (raz) słownik "znormalizowana nazwa kraju -> kod ISO" na podstawie Intl.DisplayNames.
  // Nazwy generujemy w języku danych (pl), angielskim i bieżącym locale sklepu, dzięki czemu
  // dodanie nowego kraju (nazwą PL/EN/w języku sklepu) rozpoznaje się samo, bez zmian w kodzie.
  getCountryIndex() {
    if (this._countryIndex) return this._countryIndex;
    // Oficjalne, AKTUALNE kody ISO 3166-1 alpha-2. Świadomie pomijamy przestarzałe
    // (np. CS "Serbia i Czarnogóra"), które w CLDR kolidują z aktualnymi (RS = Serbia).
    const CODES = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' ');
    const index = new Map();
    const locales = [...new Set(['pl', 'en', this.locale].filter(Boolean))];
    const dicts = locales
      .map(l => { try { return new Intl.DisplayNames([l], { type: 'region', fallback: 'none' }); } catch (e) { return null; } })
      .filter(Boolean);
    CODES.forEach(code => {
      dicts.forEach(dn => {
        let name;
        try { name = dn.of(code); } catch (e) { return; }
        if (!name || name === code) return; // brak nazwy w tym języku
        const norm = this.normalizeCountry(name);
        if (norm && !index.has(norm)) index.set(norm, code.toLowerCase());
      });
    });
    this._countryIndex = index;
    return index;
  }

  getCountryCode(countryString) {
    const raw = (countryString || '').trim();
    if (!raw) return '';
    const c = raw.toLowerCase();
    // Gołe 2-literowe kody ISO w danych (np. "GB", "NO", "SE") — użyj wprost.
    if (/^[a-z]{2}$/.test(c)) return c;
    // Automatyczne rozpoznanie pełnej nazwy (PL / EN / język sklepu) przez słownik z Intl.
    const fromIndex = this.getCountryIndex().get(this.normalizeCountry(raw));
    if (fromIndex) return fromIndex;
    // Fallback: charakterystyczne fragmenty dla skrótów i nietypowych zapisów ("USA", "UK"...).
    // UWAGA: nie używać 2-literowych fragmentów — "aUStria" łapałoby "us", "fiNLandia" -> "nl" itd.
    const has = (...frags) => frags.some(f => c.includes(f));
    if (has('pol')) return 'pl';                                  // Polska / Poland
    if (has('niem', 'germ', 'deutsch')) return 'de';             // Niemcy / Germany
    if (has('wło', 'wloch', 'ital')) return 'it';                // Włochy / Italy
    if (has('franc')) return 'fr';                                // Francja / France
    if (has('hiszp', 'spain', 'espa')) return 'es';             // Hiszpania / Spain
    if (has('wielk', 'brytan', 'united kingdom', 'great brit')) return 'gb';
    if (has('usa', 'stany', 'united states', 'zjednoczone am')) return 'us';
    if (has('turc', 'turk', 'türk')) return 'tr';               // Turcja / Turkey / Türkiye
    if (has('czech', 'czechy')) return 'cz';                     // Czechy / Czechia
    if (has('słowac', 'slowac', 'slovak')) return 'sk';         // Słowacja / Slovakia
    if (has('szwec', 'swed')) return 'se';                       // Szwecja / Sweden
    if (has('norw')) return 'no';                                 // Norwegia / Norway
    if (has('holand', 'nether', 'niderland')) return 'nl';      // Holandia / Netherlands
    if (has('belg')) return 'be';                                 // Belgia / Belgium
    if (has('chorw', 'croat')) return 'hr';                      // Chorwacja / Croatia
    if (has('szwajc', 'switz', 'suisse')) return 'ch';          // Szwajcaria / Switzerland
    if (has('austri', 'öster', 'oster')) return 'at';           // Austria (nie "australia")
    if (has('irland', 'ireland')) return 'ie';                   // Irlandia / Ireland
    if (has('grecj', 'grecja', 'greece', 'hellen')) return 'gr'; // Grecja / Greece
    if (has('węgr', 'wegr', 'hungar')) return 'hu';             // Węgry / Hungary
    if (has('rumun', 'roman')) return 'ro';                      // Rumunia / Romania
    if (has('bułg', 'bulg')) return 'bg';                        // Bułgaria / Bulgaria
    if (has('litw', 'lithuan')) return 'lt';                     // Litwa / Lithuania
    if (has('łotw', 'lotw', 'latvia')) return 'lv';            // Łotwa / Latvia
    if (has('eston')) return 'ee';                               // Estonia
    if (has('cypr', 'cyprus')) return 'cy';                      // Cypr / Cyprus
    if (has('ryko', 'puerto')) return 'pr';                      // Portoryko / Puerto Rico
    if (has('ekwad', 'ecuad')) return 'ec';                      // Ekwador / Ecuador
    if (has('japo', 'japan')) return 'jp';                       // Japonia / Japan
    if (has('dani', 'denmark')) return 'dk';                     // Dania / Denmark
    if (has('portug')) return 'pt';                              // Portugalia / Portugal
    if (has('finlan', 'finland', 'suomi')) return 'fi';        // Finlandia / Finland
    if (has('serb')) return 'rs';                               // Serbia
    if (has('luksem', 'luxem')) return 'lu';                    // Luksemburg / Luxembourg
    if (has('reunion', 'réunion')) return 're';                // Réunion
    return ''; // Fallback
  }

  getSygnetSvg(type, colorOverride = null, sygnetOnly = false, isActive = false) {
    const cat = (type || '').toLowerCase();
    let pinColor = 'black';
    let sygnetColor = 'white';
    let clipId = `clip_${Math.random().toString(36).substr(2, 9)}`;

    if (cat.includes('pro')) {
      pinColor = 'black';
      sygnetColor = '#01DF82';
    } else if (cat.includes('distr') || cat.includes('dystryb')) {
      pinColor = 'black';
      sygnetColor = 'white';
    } else {
      // Reseller — ta sama czarno-biała ikona co Exclusive Distributor
      pinColor = 'black';
      sygnetColor = 'white';
    }

    if (isActive) {
      pinColor = this.markerColor || '#01DF82';
      sygnetColor = 'black';
    } else if (colorOverride) {
      pinColor = colorOverride;
    }

    // Paths from ultracoat-sygnet.svg asset (for badges)
    const sygnetOnlyPaths = `
      <path d="M17.5751 15.9978H10.8779L14.8025 0H21.4997L17.5751 15.9978Z" fill="${sygnetColor}"/>
      <path d="M10.8779 15.9977C8.64547 15.9977 6.41307 15.9977 4.18066 15.9977C4.59938 14.286 5.01809 12.5721 5.43897 10.8604C7.25266 12.5721 9.06635 14.286 10.88 15.9999L10.8779 15.9977Z" fill="${sygnetColor}"/>
      <path d="M8.10304 0L5.43891 10.8583L0 5.73613L1.40583 0H8.10304Z" fill="${sygnetColor}"/>
    `;

    if (sygnetOnly) {
      return `
        <svg width="22" height="16" viewBox="0 0 22 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-sygnet">
          ${sygnetOnlyPaths}
        </svg>
      `;
    }

    // Diamond + Sygnet (for markers and radio cards) - Exactly matching liquid file paths
    // Added white stroke to the main background path
    return `
      <svg width="31" height="40" viewBox="0 0 31 40" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-sygnet">
        <g clip-path="url(#${clipId})">
          <path d="M23.25 0H7.75L0 13.3082L15.538 40L31 13.3459L23.25 0Z" fill="${pinColor}" stroke="white" stroke-width="2" stroke-linejoin="round"/>
          <path d="M21.068 20.7982H15.6045L18.8061 8H24.2697L21.068 20.7982Z" fill="${sygnetColor}"/>
          <path d="M15.6051 20.7982C13.7839 20.7982 11.9628 20.7982 10.1416 20.7982C10.4832 19.4289 10.8248 18.0577 11.1681 16.6884C12.6477 18.0577 14.1273 19.4289 15.6069 20.8L15.6051 20.7982Z" fill="${sygnetColor}"/>
          <path d="M13.3408 8L11.1675 16.6866L6.73047 12.5889L7.87733 8H13.3408Z" fill="${sygnetColor}"/>
        </g>
        <defs>
          <clipPath id="${clipId}">
            <rect width="31" height="40" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    `;
  }

  getCategoryBadge(category) {
    const cat = (category || '').toLowerCase();
    const icon = this.getSygnetSvg(cat, null, true); // Sygnet only for badges
    
    if (cat.includes('pro')) {
      return `<span class="partner-card__badge partner-card__badge--pro">${icon}ULTRACOAT PRO</span>`;
    }
    if (cat.includes('distr') || cat.includes('dystryb')) {
      return `<span class="partner-card__badge partner-card__badge--distributor">${icon}EXCLUSIVE DISTRIBUTOR</span>`;
    }
    if (cat.includes('resel')) {
      return `<span class="partner-card__badge partner-card__badge--reseller">${icon}RESELLER</span>`;
    }
    return `<span class="partner-card__badge partner-card__badge--reseller">${icon}${category.toUpperCase()}</span>`;
  }

  renderPartnersFromJson(data) {
    const listContainer = this.elements.partnerList;
    if (listContainer) listContainer.innerHTML = '';
    
    // 1. Build this.partners array directly from the source data
    this.partners = data.map((partner, index) => {
      const lat = parseFloat(partner.lat);
      const lng = parseFloat(partner.lng);
      
      const getString = (val) => {
        if (typeof val === 'string') return val;
        if (val && typeof val === 'object') {
          if (val.value) return String(val.value);
          // Handle cases where Shopify might return a localized object or similar
          return val.toString() === '[object Object]' ? '' : String(val);
        }
        return val ? String(val) : '';
      };

      const name = getString(partner.name).trim();
      const city = getString(partner.city).trim();
      const address = getString(partner.address).trim();
      const postalCode = (getString(partner.post_code) || getString(partner.postal_code)).trim();
      const country = getString(partner.country).trim();
      
      return {
        index: index, // We'll re-assign this after sorting
        category: (getString(partner.category) || '').toLowerCase().replace(/\s+/g, '-'),
        country: country,
        city: city,
        postal_code: postalCode,
        position: (!isNaN(lat) && !isNaN(lng)) ? { lat, lng } : null,
        name: name,
        address: address,
        phone: getString(partner.phone) || null,
        website: getString(partner.website) || null,
        priority: partner.priority,
        // Combined search string including all relevant fields
        // + zlokalizowana nazwa kraju i kod ISO, żeby szukanie działało w każdym języku
        searchString: `${name.toLowerCase()} ${address.toLowerCase()} ${city.toLowerCase()} ${postalCode.toLowerCase()} ${country.toLowerCase()} ${this.countrySearchTerms(country)}`
      };
    });

    // 2. Sort the populated partners array by priority
    this.partners.sort((a, b) => {
      const pA = a.priority !== null && a.priority !== undefined ? Number(a.priority) : Infinity;
      const pB = b.priority !== null && b.priority !== undefined ? Number(b.priority) : Infinity;
      
      if (pA !== pB) return pA - pB;
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    // 3. Render HTML from the sorted partners array
    this.partners.forEach((partner, index) => {
      // Re-assign index based on final sorted position
      partner.index = index;

      let websiteDisplay = partner.website ? partner.website.replace('https://', '').replace('http://', '') : '';
      if (partner.website && (partner.website.toLowerCase().includes('facebook.com') || partner.website.toLowerCase().includes('instagram.com'))) {
        websiteDisplay = 'www';
      } else if (websiteDisplay.length > 30) {
         websiteDisplay = 'www';
      }

      const iconPlace = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.5 0H7.5L3 7.98492L12.0221 24L21 8.00754L16.5 0ZM12 18.3676L6.17647 7.98492L9.08824 2.8049H14.9118L17.8235 8.00754L12 18.3676Z" fill="black"/><path d="M14.5367 7.98492L13.2793 10.2469H10.7205L9.46313 7.98492L10.7205 5.7229H13.2793L14.5367 7.98492Z" fill="black"/></svg>`;
      const iconPhone = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_266_759)"><path d="M5.64195 3.66724L9.41772 7.44301L6.31465 10.5461L13.4539 17.6853L16.557 14.5822L20.3327 18.358L17.8156 21.3092H12.6727L2.69078 11.3273V6.18442L5.64195 3.66724ZM5.77215 -2.86996e-05L3.88427 1.62746L0.933092 4.14464L0 4.94753V12.434L0.781194 13.2152L10.7631 23.1971L11.5443 23.9783H19.0307L19.8336 23.0452L22.3508 20.094L23.9783 18.2061L22.2206 16.4484L18.4448 12.6727L16.5353 10.7631L14.6257 12.6727L13.4105 13.8879L10.0687 10.5461L11.2839 9.33089L13.1935 7.42131L11.2839 5.51172L7.50814 1.73596L5.75045 -0.0217285L5.77215 -2.86996e-05Z" fill="black"/></g><defs><clipPath id="clip0_266_759"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>`;
      const iconWorld = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_266_752)"><path d="M20.3582 15.2686V24H0V3.64185H10.6315V6.19793H2.53346V21.4665H17.8021V15.2686H20.3582Z" fill="black"/><path d="M21.4441 8.48254H24.0002V7.08009L21.4441 4.54663V8.48254Z" fill="black"/><path d="M24.0001 0V1.92271L21.444 4.45617L10.6542 15.2686H10.6316V11.672L19.7701 2.55608H15.5175V0H24.0001Z" fill="black"/></g><defs><clipPath id="clip0_266_752"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>`;

      const countryCode = this.getCountryCode(partner.country);
      const flagHtml = countryCode ? `<img src="https://cdn.shopify.com/static/images/flags/${countryCode}.svg" alt="${partner.country}" class="partner-card__flag">` : '🌍';
      const badge = this.getCategoryBadge(partner.category);

      const cardHtmlMobile = `
        <div class="partner-card__mobile">
          <div class="partner-card__header">
            <div class="partner-card__header-left">
              <div class="partner-card__meta">
                <span class="partner-card__country ${countryCode ? 'has-flag' : ''}" title="${partner.country || ''}">${flagHtml}</span>
                ${badge}
              </div>
              <span class="partner-card__title">${partner.name}</span>
              <div class="partner-card__city">${partner.city || ''}</div>
            </div>
            <button type="button" class="partner-card__toggle" aria-label="Rozwiń szczegóły">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="partner-card__main-info">
              <div class="partner-card__info">
                ${iconPlace}
                <div class="partner-card__address-details">
                  ${partner.address ? `${partner.address}` : ''}
                </div>
              </div>
          </div>
          <div class="partner-card__collapsible">
            <div class="partner-card__collapsible-inner">
              <div class="partner-card__contact-details">
                ${partner.phone ? `<div class="partner-card__info">${iconPhone}<a href="tel:${partner.phone}">${partner.phone}</a></div>` : ''}
                ${partner.website ? `<div class="partner-card__info">${iconWorld}<a href="${partner.website}" target="_blank" rel="noopener">${websiteDisplay}</a></div>` : ''}
              </div>

              <div class="partner-card__actions">
                <button type="button" class="button button--secondary button--small w-full" data-action="show-on-map">
                  Pokaż na mapie
                </button>
              </div>
            </div>
          </div>
        </div>
      `;

      const cardHtmlDesktop = `
        <div class="partner-card__desktop">
          <div class="partner-card__header">
            ${badge}
            <span class="partner-card__country ${countryCode ? 'has-flag' : ''}" title="${partner.country || ''}">${flagHtml}</span>
          </div>
          <span class="partner-card__title">${partner.name}</span>
          <div class="partner-card__info">
            ${iconPlace}
            <div class="partner-card__address-details">
              ${partner.address ? `${partner.address}` : ''}
              <br>
              ${partner.postal_code || ''} ${partner.city || ''}
            </div>
          </div>
          <div class="partner-card__contact-details">
            ${partner.phone ? `<div class="partner-card__info">${iconPhone}<a href="tel:${partner.phone}">${partner.phone}</a></div>` : ''}
            ${partner.website ? `<div class="partner-card__info">${iconWorld}<a href="${partner.website}" target="_blank" rel="noopener">${websiteDisplay}</a></div>` : ''}
          </div>
        </div>
      `;

      if (listContainer) {
        // Create and store the element reference
        const card = document.createElement('div');
        card.className = 'partner-card';
        card.dataset.partnerCard = '';
        card.dataset.index = index;
        card.dataset.lat = partner.position?.lat || '';
        card.dataset.lng = partner.position?.lng || '';
        card.dataset.category = partner.category;
        card.dataset.country = partner.country;
        card.innerHTML = cardHtmlMobile + cardHtmlDesktop;
        listContainer.appendChild(card);
        
        partner.element = card;
      }
    });

    this.elements.partnerCards = Array.from(this.querySelectorAll('[data-partner-card]'));

    // After loading JSON data and rendering, initialize geocoding for missing positions
    this.partners.forEach(partner => {
      if (!partner.position) {
        this.geocodeAddress(partner);
      }
    });
  }

  initPartnersFromDom() {
    // Only used as fallback when JSON/Metaobjects data is not present
    if (this.partners.length > 0) return;

    this.elements.partnerCards = Array.from(this.querySelectorAll('[data-partner-card]'));
    
    this.partners = this.elements.partnerCards.map(card => {
      const idx = parseInt(card.dataset.index);
      const lat = parseFloat(card.dataset.lat);
      const lng = parseFloat(card.dataset.lng);
      
      const nameRaw = (card.querySelector('.partner-card__title')?.innerText || '').trim();
      const addressRaw = (card.querySelector('.partner-card__address-details')?.innerText || '').trim();
      
      // Extract rich data if possible
      const phoneElem = card.querySelector('a[href^="tel:"]');
      const websiteElem = card.querySelector('.partner-card__contact-details a[target="_blank"]');
      
      return {
        index: idx,
        element: card,
        category: card.dataset.category,
        country: card.dataset.country,
        city: card.querySelector('.partner-card__city')?.innerText.trim() || '',
        postal_code: card.dataset.postalCode || '',
        position: (!isNaN(lat) && !isNaN(lng)) ? { lat, lng } : null,
        name: nameRaw,
        address: addressRaw.split('\n')[0],
        phone: phoneElem ? phoneElem.innerText.trim() : null,
        website: websiteElem ? websiteElem.getAttribute('href') : null,
        searchString: `${nameRaw.toLowerCase()} ${addressRaw.toLowerCase()} ${(card.dataset.country||'').toLowerCase()} ${this.countrySearchTerms(card.dataset.country||'')}`
      };
    });

    this.partners.forEach(partner => {
      if (!partner.position) {
        this.geocodeAddress(partner);
      }
    });
  }

  // Memoizowany Intl.DisplayNames dla bieżącego języka (albo null na starych przeglądarkach).
  getRegionNames() {
    if (this._regionNames === undefined) {
      try { this._regionNames = new Intl.DisplayNames([this.locale], { type: 'region' }); }
      catch (e) { this._regionNames = null; }
    }
    return this._regionNames;
  }

  // Zlokalizowana nazwa kraju w bieżącym języku ('' gdy nieznany kod).
  localizedCountry(raw) {
    const code = this.getCountryCode(raw);
    const rn = this.getRegionNames();
    if (code && rn) {
      try { return rn.of(code.toUpperCase()) || ''; } catch (e) { /* nieznany kod */ }
    }
    return '';
  }

  // Stały klucz kraju do filtrowania i grupowania: kod ISO gdy znany, inaczej surowy tekst.
  // Odporny na niespójne dane ("Wielka Brytania" / "United Kingdom" / "GB" -> "gb").
  countryKey(raw) {
    const t = (raw || '').trim();
    if (!t) return '';
    return this.getCountryCode(t) || t.toLowerCase();
  }

  // Dodatkowe hasła do wyszukiwarki: kod ISO + nazwa w bieżącym języku (np. "be belgium"),
  // dzięki czemu na EN działa szukanie "Belgium", a nie tylko surowe "Belgia".
  countrySearchTerms(raw) {
    const parts = [];
    const code = this.getCountryCode(raw);
    if (code) parts.push(code);
    const localized = this.localizedCountry(raw);
    if (localized) parts.push(localized);
    return parts.join(' ').toLowerCase();
  }

  initCountryDropdown() {
    if (!this.elements.countrySelect) return;

    // Grupuj po stałym kluczu, żeby scalić duplikaty (kod ISO / różne zapisy nazwy).
    const groups = new Map(); // key -> { value, label }
    this.partners.forEach(p => {
      const raw = (p.country || '').trim();
      if (!raw) return;
      const key = this.countryKey(raw);
      if (groups.has(key)) return;
      groups.set(key, { value: key, label: this.localizedCountry(raw) || raw });
    });

    [...groups.values()]
      .sort((a, b) => a.label.localeCompare(b.label, this.locale))
      .forEach(g => {
        const option = document.createElement('option');
        option.value = g.value;
        option.innerText = g.label;
        this.elements.countrySelect.appendChild(option);
      });
  }

  loadGoogleMaps() {
    return new Promise((resolve) => {
      if (window.google && window.google.maps && window.markerClusterer) {
        resolve();
        return;
      }

      let scriptsLoaded = 0;
      const totalScripts = 2;
      const onScriptLoad = () => {
        scriptsLoaded++;
        if (scriptsLoaded === totalScripts) resolve();
      };

      // 1. Google Maps
      if (!(window.google && window.google.maps)) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=geometry`;
        script.async = true;
        script.defer = true;
        script.onload = onScriptLoad;
        document.head.appendChild(script);
      } else {
        onScriptLoad();
      }

      // 2. Marker Clusterer
      if (!window.markerClusterer) {
        const clustererScript = document.createElement('script');
        clustererScript.src = `https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js`;
        clustererScript.async = true;
        clustererScript.defer = true;
        clustererScript.onload = onScriptLoad;
        document.head.appendChild(clustererScript);
      } else {
        onScriptLoad();
      }
    });
  }

  initMap() {
    const defaultCenter = { lat: 52, lng: 19 }; // Europe/Poland center


    const mapStyles = [{"featureType":"administrative","elementType":"all","stylers":[{"visibility":"on"},{"lightness":33}]},{"featureType":"administrative","elementType":"labels","stylers":[{"saturation":"-100"}]},{"featureType":"administrative","elementType":"labels.text","stylers":[{"gamma":"0.75"}]},{"featureType":"administrative.neighborhood","elementType":"labels.text.fill","stylers":[{"lightness":"-37"}]},{"featureType":"landscape","elementType":"geometry","stylers":[{"color":"#f9f9f9"}]},{"featureType":"landscape.man_made","elementType":"geometry","stylers":[{"saturation":"-100"},{"lightness":"40"},{"visibility":"off"}]},{"featureType":"landscape.natural","elementType":"labels.text.fill","stylers":[{"saturation":"-100"},{"lightness":"-37"}]},{"featureType":"landscape.natural","elementType":"labels.text.stroke","stylers":[{"saturation":"-100"},{"lightness":"100"},{"weight":"2"}]},{"featureType":"landscape.natural","elementType":"labels.icon","stylers":[{"saturation":"-100"}]},{"featureType":"poi","elementType":"geometry","stylers":[{"saturation":"-100"},{"lightness":"80"}]},{"featureType":"poi","elementType":"labels","stylers":[{"saturation":"-100"},{"lightness":"0"}]},{"featureType":"poi.attraction","elementType":"geometry","stylers":[{"lightness":"-4"},{"saturation":"-100"}]},{"featureType":"poi.park","elementType":"geometry","stylers":[{"color":"#c5dac6"},{"visibility":"on"},{"saturation":"-95"},{"lightness":"62"}]},{"featureType":"poi.park","elementType":"labels","stylers":[{"visibility":"on"},{"lightness":20}]},{"featureType":"road","elementType":"all","stylers":[{"lightness":20}]},{"featureType":"road","elementType":"labels","stylers":[{"saturation":"-100"},{"gamma":"1.00"}]},{"featureType":"road","elementType":"labels.text","stylers":[{"gamma":"0.50"}]},{"featureType":"road","elementType":"labels.icon","stylers":[{"saturation":"-100"},{"gamma":"0.50"}]},{"featureType":"road.highway","elementType":"geometry","stylers":[{"color":"#c5c6c6"},{"saturation":"-100"}]},{"featureType":"road.highway","elementType":"geometry.stroke","stylers":[{"lightness":"-13"}]},{"featureType":"road.highway","elementType":"labels.icon","stylers":[{"lightness":"0"},{"gamma":"1.09"}]},{"featureType":"road.arterial","elementType":"geometry","stylers":[{"color":"#e4d7c6"},{"saturation":"-100"},{"lightness":"47"}]},{"featureType":"road.arterial","elementType":"geometry.stroke","stylers":[{"lightness":"-12"}]},{"featureType":"road.arterial","elementType":"labels.icon","stylers":[{"saturation":"-100"}]},{"featureType":"road.local","elementType":"geometry","stylers":[{"color":"#fbfaf7"},{"lightness":"77"}]},{"featureType":"road.local","elementType":"geometry.fill","stylers":[{"lightness":"-5"},{"saturation":"-100"}]},{"featureType":"road.local","elementType":"geometry.stroke","stylers":[{"saturation":"-100"},{"lightness":"-15"}]},{"featureType":"transit.station.airport","elementType":"geometry","stylers":[{"lightness":"47"},{"saturation":"-100"}]},{"featureType":"water","elementType":"all","stylers":[{"visibility":"on"},{"color":"#d3d3d3"}]},{"featureType":"water","elementType":"geometry","stylers":[{"saturation":"0"}]},{"featureType":"water","elementType":"labels.text.fill","stylers":[{"lightness":"-42"},{"saturation":"17"}]},{"featureType":"water","elementType":"labels.text.stroke","stylers":[{"lightness":"61"}]}];
    
    this.map = new google.maps.Map(this.elements.canvas, {
      zoom: 3,
      center: defaultCenter,
      styles: mapStyles,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      minZoom: 2,
      backgroundColor: '#ffffff'
    });

    this.map.addListener('click', () => {
      this.hideMobilePopup();
    });

    const renderer = {
      render: ({ count, position }) => {
        let color = '#01df82';
        if (count > 100) color = '#00854d';
        else if (count > 50) color = '#00a45f';
        else if (count > 10) color = '#00c371';

        const svg = `
          <svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <filter id="blurFilter" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur in="SourceGraphic" stdDeviation="3" />
              </filter>
            </defs>
            <circle cx="30" cy="30" r="22" fill="${color}" opacity="0.4" filter="url(#blurFilter)" />
            <circle cx="30" cy="30" r="18" fill="${color}" />
            <text x="50%" y="50%" text-anchor="middle" fill="white" font-size="14" font-weight="bold" font-family="Arial" dy=".3em">${count}</text>
          </svg>`;

        return new google.maps.Marker({
          position,
          icon: {
            url: `data:image/svg+xml;base64,${btoa(svg)}`,
            scaledSize: new google.maps.Size(60, 60),
            anchor: new google.maps.Point(30, 30)
          },
          label: '',
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
      }
    };

    // Setup Custom Collision Overlay to hide highly overlapping markers
    this.collisionOverlay = new google.maps.OverlayView();
    this.collisionOverlay.onAdd = () => {};
    this.collisionOverlay.onRemove = () => {};
    this.collisionOverlay.draw = () => {
      this.updateMarkerCollisions();
    };
    this.collisionOverlay.setMap(this.map);

    this.map.addListener('idle', () => {
      this.updateMarkerCollisions();
    });
    // Detect panning to show "Search this area"
    this.map.addListener('dragend', () => {
      if ((this.state.category !== 'all' && this.state.category !== this.forcedCategory) || this.state.country !== 'all' || this.state.keyword) {
        this.searchAreaBtn.classList.add('visible');
      }
    });
    this.map.addListener('zoom_changed', () => {
      if ((this.state.category !== 'all' && this.state.category !== this.forcedCategory) || this.state.country !== 'all' || this.state.keyword) {
        this.searchAreaBtn.classList.add('visible');
      }
    });
  }

  switchTab(tabName) {
    if (window.innerWidth >= 768) return;
    if (!this.elements.content || !this.elements.mobileTabButtons) return;
    
    this.elements.content.dataset.activeTab = tabName;
    this.elements.mobileTabButtons.forEach(btn => {
      btn.classList.toggle('active', btn.dataset.tab === tabName);
    });

    // Scroll to section top on mobile to prevent being "lost" if the list is long
    const rect = this.getBoundingClientRect();
    const buffer = 120; // Account for sticky headers/nav
    window.scrollTo({
      top: window.pageYOffset + rect.top - buffer,
      behavior: 'smooth'
    });

    if (tabName === 'map' && this.map) {
      google.maps.event.trigger(this.map, 'resize');
      this.fitMapToBounds();
    }
  }

  showMobilePopup(partnerIndex) {
    if (window.innerWidth >= 768 || !this.elements.mobilePopup) return;

    // Use .find to match the explicitly assigned partner index (since the array may have been sorted)
    const partner = this.partners.find(p => p.index === parseInt(partnerIndex));
    if (!partner) return;

    // We reuse the same card HTML logic as in renderPartnersFromJson
    const countryCode = this.getCountryCode(partner.country);
    const flagHtml = countryCode ? `<img src="https://cdn.shopify.com/static/images/flags/${countryCode}.svg" alt="${partner.country}" class="partner-card__flag">` : '🌍';
    const badge = this.getCategoryBadge(partner.category);
    
    // Exact same icons as in renderPartnersFromJson
    const iconPlace = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.5 0H7.5L3 7.98492L12.0221 24L21 8.00754L16.5 0ZM12 18.3676L6.17647 7.98492L9.08824 2.8049H14.9118L17.8235 8.00754L12 18.3676Z" fill="black"/><path d="M14.5367 7.98492L13.2793 10.2469H10.7205L9.46313 7.98492L10.7205 5.7229H13.2793L14.5367 7.98492Z" fill="black"/></svg>`;
    const iconPhone = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_266_759)"><path d="M5.64195 3.66724L9.41772 7.44301L6.31465 10.5461L13.4539 17.6853L16.557 14.5822L20.3327 18.358L17.8156 21.3092H12.6727L2.69078 11.3273V6.18442L5.64195 3.66724ZM5.77215 -2.86996e-05L3.88427 1.62746L0.933092 4.14464L0 4.94753V12.434L0.781194 13.2152L10.7631 23.1971L11.5443 23.9783H19.0307L19.8336 23.0452L22.3508 20.094L23.9783 18.2061L22.2206 16.4484L18.4448 12.6727L16.5353 10.7631L14.6257 12.6727L13.4105 13.8879L10.0687 10.5461L11.2839 9.33089L13.1935 7.42131L11.2839 5.51172L7.50814 1.73596L5.75045 -0.0217285L5.77215 -2.86996e-05Z" fill="black"/></g><defs><clipPath id="clip0_266_759"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>`;
    const iconWorld = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_266_752)"><path d="M20.3582 15.2686V24H0V3.64185H10.6315V6.19793H2.53346V21.4665H17.8021V15.2686H20.3582Z" fill="black"/><path d="M21.4441 8.48254H24.0002V7.08009L21.4441 4.54663V8.48254Z" fill="black"/><path d="M24.0001 0V1.92271L21.444 4.45617L10.6542 15.2686H10.6316V11.672L19.7701 2.55608H15.5175V0H24.0001Z" fill="black"/></g><defs><clipPath id="clip0_266_752"><rect width="24" height="24" fill="white"/></clipPath></defs></svg>`;

    let websiteDisplay = partner.website ? partner.website.replace('https://', '').replace('http://', '') : '';
    if (partner.website && (partner.website.toLowerCase().includes('facebook.com') || partner.website.toLowerCase().includes('instagram.com'))) {
      websiteDisplay = 'www';
    } else if (websiteDisplay.length > 30) {
       websiteDisplay = 'www';
    }
    this.elements.mobilePopupBody.innerHTML = `
      <div class="partner-card">
        <div class="partner-card__mobile" style="display: block !important;">
          <div class="partner-card__header">
            <div class="partner-card__header-left">
              <div class="partner-card__meta">
                <span class="partner-card__country ${countryCode ? 'has-flag' : ''}" title="${partner.country || ''}">${flagHtml}</span>
                ${badge}
              </div>
              <span class="partner-card__title">${partner.name || 'Brak nazwy'}</span>
              <div class="partner-card__city">${partner.city || ''}</div>
            </div>
            <button type="button" class="partner-card__toggle" aria-label="Rozwiń szczegóły">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 9L12 15L18 9" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="partner-card__main-info">
              <div class="partner-card__info">
                ${iconPlace}
                <div class="partner-card__address-details">
                  ${partner.address ? `${partner.address}` : 'Brak adresu'}
                  ${partner.postal_code || partner.post_code ? `<br>${partner.postal_code || partner.post_code} ${partner.city || ''}` : ''}
                </div>
              </div>
          </div>
          <div class="partner-card__collapsible">
            <div class="partner-card__collapsible-inner">
              <div class="partner-card__contact-details">
                ${partner.phone ? `<div class="partner-card__info">${iconPhone}<a href="tel:${partner.phone}">${partner.phone}</a></div>` : ''}
                ${partner.website ? `<div class="partner-card__info">${iconWorld}<a href="${partner.website}" target="_blank" rel="noopener">${websiteDisplay}</a></div>` : ''}
              </div>
              <div class="partner-card__actions">
                <button type="button" class="button button--secondary button--small w-full" data-action="center-on-map">
                  Wyśrodkuj na mapie
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;

    // Add toggle listener
    const card = this.elements.mobilePopupBody.querySelector('.partner-card');
    const toggle = card.querySelector('.partner-card__toggle');
    if (toggle) {
      toggle.addEventListener('click', () => {
        card.classList.toggle('expanded');
      });
    }

    // Add listener for the "Center on map" button in popup
    const centerBtn = this.elements.mobilePopupBody.querySelector('[data-action="center-on-map"]');
    if (centerBtn) {
      centerBtn.addEventListener('click', () => {
        this.highlightMarker(partnerIndex, false, false);
      });
    }

    this.elements.mobilePopup.style.display = 'block';
  }

  hideMobilePopup() {
    if (this.elements.mobilePopup) {
      this.elements.mobilePopup.style.display = 'none';
    }
  }

  geocodeAddress(partner) {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: partner.address }, (results, status) => {
      if (status === 'OK') {
        partner.position = results[0].geometry.location;
        this.updateMarker(partner);
      }
    });
  }

  addEventListeners() {
    // Mobile Tabs
    this.elements.mobileTabButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        this.switchTab(btn.dataset.tab);
      });
    });

    // Radio Cards (Categories)
    this.elements.radioCards.forEach(card => {
      card.addEventListener('click', (e) => {
        const filterStr = card.dataset.filter;
        
        // If clicking active, deselect (reset to 'all')
        if (this.state.category === filterStr) {
          this.state.category = 'all';
          card.classList.remove('active');
        } else {
          this.state.category = filterStr;
          this.elements.radioCards.forEach(c => c.classList.remove('active'));
          card.classList.add('active');
        }
        
        this.state.boundsFilter = null; // Clear manual bounds when changing filters
        this.searchAreaBtn.classList.remove('visible');
        this.filterAndSortPartners();
        this.fitMapToBounds();
      });
    });

    // Country Select
    if (this.elements.countrySelect) {
      this.elements.countrySelect.addEventListener('change', (e) => {
        this.state.country = e.target.value;
        this.state.boundsFilter = null;
        
        if (this.elements.resetCountryBtn) {
          if (this.state.country !== 'all') {
            this.elements.countrySelect.classList.add('has-selection');
            this.elements.resetCountryBtn.style.display = 'block';
          } else {
            this.elements.countrySelect.classList.remove('has-selection');
            this.elements.resetCountryBtn.style.display = 'none';
          }
        }

        this.searchAreaBtn.classList.remove('visible');
        this.filterAndSortPartners();
        this.fitMapToBounds();
      });
    }

    // Country Reset
    if (this.elements.resetCountryBtn) {
      this.elements.resetCountryBtn.addEventListener('click', () => {
        this.state.country = 'all';
        if (this.elements.countrySelect) {
          this.elements.countrySelect.value = 'all';
          this.elements.countrySelect.classList.remove('has-selection');
        }
        this.elements.resetCountryBtn.style.display = 'none';
        
        this.state.boundsFilter = null;
        this.searchAreaBtn.classList.remove('visible');
        this.filterAndSortPartners();
        this.fitMapToBounds();
      });
    }

    // Keyword Search
    if (this.elements.searchInput) {
      this.elements.searchInput.addEventListener('input', (e) => {
        this.state.keyword = e.target.value.toLowerCase().trim();
        if (this.elements.resetButton) {
          this.elements.resetButton.style.display = this.state.keyword ? 'flex' : 'none';
        }
        this.state.boundsFilter = null;
        this.searchAreaBtn.classList.remove('visible');
        const visibleCount = this.filterAndSortPartners();

        if (this.state.keyword.length > 0 && visibleCount > 0) {
          this.fitMapToBounds();
        } else if (this.state.keyword.length === 0) {
          this.fitMapToBounds();
        }
      });
    }

    // Keyword Reset
    if (this.elements.resetButton) {
      this.elements.resetButton.addEventListener('click', () => {
        this.elements.searchInput.value = '';
        this.state.keyword = '';
        this.elements.resetButton.style.display = 'none';
        this.state.boundsFilter = null;
        this.searchAreaBtn.classList.remove('visible');
        this.filterAndSortPartners();
        this.fitMapToBounds();
      });
    }

    // Partner Cards Click (Accordion + Map Highlight)
    this.elements.partnerList.addEventListener('click', (e) => {
      const card = e.target.closest('[data-partner-card]');
      if (!card) return;

      const index = parseInt(card.dataset.index);
      const isToggle = e.target.closest('.partner-card__toggle');
      const isShowOnMap = e.target.closest('[data-action="show-on-map"]');

      // Mobile Accordion Logic
      if (window.innerWidth < 768) {
        // Let links (tel, www) work without side effects
        if (e.target.closest('a')) return;

        if (isShowOnMap) {
          this.switchTab('map');
          this.highlightMarker(index, false, false);
          this.showMobilePopup(index);
          return;
        }

        // Toggle button or any other tap on the card — only accordion, no page scroll
        this.elements.partnerCards.forEach(c => {
          if (c !== card) c.classList.remove('expanded');
        });
        card.classList.toggle('expanded');
        return;
      }

      // Desktop or direct card click
      this.highlightMarker(index, false, false);
    });

    // Mobile Popup Close
    if (this.elements.mobilePopupClose) {
      this.elements.mobilePopupClose.addEventListener('click', () => {
        this.hideMobilePopup();
      });
    }

    // Search Area Button
    this.searchAreaBtn.addEventListener('click', () => {
      this.state.boundsFilter = this.map.getBounds();
      
      // Clear all other filters visually and in state
      this.state.category = this.forcedCategory;
      this.elements.radioCards.forEach(c => c.classList.remove('active'));
      
      this.state.country = 'all';
      if(this.elements.countrySelect) {
        this.elements.countrySelect.value = 'all';
        this.elements.countrySelect.classList.remove('has-selection');
      }
      if(this.elements.resetCountryBtn) {
        this.elements.resetCountryBtn.style.display = 'none';
      }
      
      this.state.keyword = '';
      if(this.elements.searchInput) this.elements.searchInput.value = '';
      if(this.elements.resetButton) this.elements.resetButton.style.display = 'none';

      this.searchAreaBtn.classList.remove('visible');
      this.filterAndSortPartners();
    });
  }

  createSvgMarkerIcon(type, isActive = false) {
    const cacheKey = `${type}-${isActive}`;
    if (this.iconCache.has(cacheKey)) {
      return this.iconCache.get(cacheKey);
    }

    let scale = isActive ? 1.2 : 0.9;
    const svg = this.getSygnetSvg(type, null, false, isActive);
    const width = 31 * scale;
    const height = 40 * scale;

    const scaledSvg = svg
      .replace(/width="\d+"/, `width="${width}"`)
      .replace(/height="\d+"/, `height="${height}"`);

    const icon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(scaledSvg),
      scaledSize: new google.maps.Size(width, height),
      anchor: new google.maps.Point(width / 2, height)
    };

    this.iconCache.set(cacheKey, icon);
    return icon;
  }

  renderMarkers() {
    this.markers.forEach(m => m.setMap(null));
    this.markers = [];
    // if (this.markerClusterer) this.markerClusterer.clearMarkers();

    this.partners.forEach(partner => {
      if (partner.position) {
        const marker = new google.maps.Marker({
          map: this.map,
          position: partner.position,
          title: partner.name,
          icon: this.createSvgMarkerIcon(partner.category)
        });

        marker.addListener('click', () => {
          this.highlightMarker(partner.index, true, true); 
          if (window.innerWidth < 768) {
            this.showMobilePopup(partner.index);
          }
        });

        this.markers[partner.index] = marker;
      }
    });

    /*
    if (this.markerClusterer) {
      this.markerClusterer.addMarkers(this.markers.filter(m => m));
    }
    */
  }

  updateMarker(partner) {
    if (this.markers[partner.index]) {
      this.markers[partner.index].setPosition(partner.position);
    } else {
      this.renderMarkers();
      this.filterAndSortPartners();
    }
  }

  getSynonyms(word) {
    const synonyms = {
      'italy': ['włochy', 'italia', 'italie', 'it'],
      'włochy': ['italy', 'italia', 'italie', 'it'],
      'rome': ['rzym', 'roma'],
      'rzym': ['rome', 'roma'],
      'germany': ['niemcy', 'deutschland', 'de'],
      'niemcy': ['germany', 'deutschland', 'de'],
      'spain': ['hiszpania', 'espana', 'es'],
      'hiszpania': ['spain', 'espana', 'es'],
      'uk': ['wielka brytania', 'united kingdom', 'england', 'anglia'],
      'wielka brytania': ['uk', 'united kingdom', 'england', 'anglia']
    };
    
    let terms = [word];
    for (const [key, variants] of Object.entries(synonyms)) {
      // Only trigger synonym injection if it's an exact word match, to prevent "de" matching "detailing"
      const matchRegex = new RegExp(`\\b(${key}|${variants.join('|')})\\b`, 'i');
      if (matchRegex.test(word)) {
        terms = terms.concat(variants);
        terms.push(key);
      }
    }
    return [...new Set(terms)]; // unique
  }

  filterAndSortPartners() {
    let visibleCount = 0;
    
    // Split search into individual words so we can perform an AND search
    const searchWords = this.state.keyword ? this.state.keyword.split(/\s+/).filter(w => w.length > 0) : [];

    this.partners.forEach(partner => {
      let isVisible = true;

      // 1. Category Filter Logic
      if (this.state.category !== 'all') {
        const pCat = partner.category;
        
        if (this.forcedCategory !== 'all') {
          // Strict match if forced via section settings
          if (this.forcedCategory === 'reseller' && !pCat.includes('resel')) {
            isVisible = false;
          } else if (this.forcedCategory === 'distributor' && (!pCat.includes('distr') && !pCat.includes('dystryb'))) {
            isVisible = false;
          } else if (this.forcedCategory === 'ultracoat-pro' && !pCat.includes('pro')) {
            isVisible = false;
          }
        } else {
          // Strict match via radio buttons
          if (this.state.category === 'reseller') {
            if (!pCat.includes('resel') && !pCat.includes('distr') && !pCat.includes('dystryb')) {
              isVisible = false;
            }
          } else if (this.state.category === 'ultracoat-pro') {
            if (!pCat.includes('pro')) isVisible = false;
          } else if (this.state.category === 'distributor') {
            if (!pCat.includes('distr') && !pCat.includes('dystryb')) isVisible = false;
          }
        }
      }

      // 2. Country Filter (porównanie po stałym kluczu — odporne na język/zapis)
      if (isVisible && this.state.country !== 'all') {
        if (this.countryKey(partner.country) !== this.state.country) {
          isVisible = false;
        }
      }

      // 3. Keyword Search (Every typed word must match the partner OR its synonyms)
      if (isVisible && searchWords.length > 0) {
        for (const word of searchWords) {
          const wordSynonyms = this.getSynonyms(word);
          const matchesWord = wordSynonyms.some(term => partner.searchString.includes(term));
          if (!matchesWord) {
            isVisible = false;
            break;
          }
        }
      }

      // 4. Bounds Filter (from "Search Area" button)
      if (isVisible && this.state.boundsFilter && partner.position) {
        if (!this.state.boundsFilter.contains(partner.position)) {
          isVisible = false;
        }
      }

      // Apply visibility to UI Elements
      partner.element.style.display = isVisible ? 'block' : 'none';

      if (this.markers[partner.index]) {
        this.markers[partner.index]._matchesFilter = isVisible;
        
        // Hide immediately if it fails the filter
        if (!isVisible && this.markers[partner.index].getMap()) {
          this.markers[partner.index].setMap(null);
        }
        
        // Reset active state icon just in case
        this.markers[partner.index].setIcon(this.createSvgMarkerIcon(partner.category, false));
      }

      if (isVisible) visibleCount++;
    });

    this.updateMarkerCollisions();

    // Update Counter Text
    if (this.elements.resultsCount) {
      this.elements.resultsCount.innerText = visibleCount;
      if (this.elements.noResults) {
        this.elements.noResults.style.display = visibleCount === 0 ? 'block' : 'none';
      }
    }

    return visibleCount;
  }

  fitMapToBounds() {
    if (!this.map || this.state.boundsFilter) return; // Don't auto-fit if user manually searched area
    
    const bounds = new google.maps.LatLngBounds();
    let hasVisibleMarkers = false;

    this.partners.forEach(p => {
      if (p.element.style.display !== 'none' && p.position) {
        bounds.extend(p.position);
        hasVisibleMarkers = true;
      }
    });

    const isAnyFilterActive = this.state.country !== 'all' || this.state.keyword || (this.state.category !== 'all' && this.state.category !== this.forcedCategory);

    if (hasVisibleMarkers && isAnyFilterActive) {
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        // Only one marker, pan to it and zoom
        this.map.setCenter(bounds.getNorthEast());
        this.map.setZoom(14);
      } else {
        this.map.fitBounds(bounds);
        // Optional: Ensure it doesn't zoom in *too* far
        const listener = google.maps.event.addListener(this.map, "idle", () => {
          if (this.map.getZoom() > 14) this.map.setZoom(14);
          google.maps.event.removeListener(listener);
        });
      }
    } else {
      // Complete world view if empty or resetting all filters
      // This ensures we always return to the requested centered global view
      if (this.userLocation) {
        this.map.setCenter(this.userLocation);
        this.map.setZoom(8);
      } else {
        this.map.setCenter({ lat: 52, lng: 19 });
        this.map.setZoom(this.initialZoom || 5);
      }
    }
  }

  highlightMarker(index, scrollList = true, fromMapClick = false) {
    const partner = this.partners.find(p => p.index === index);
    if (!partner || !partner.position) return;

    // Robust page scroll: Ensure section is visible with buffer for sticky headers
    const isMobile = window.innerWidth < 768;
    const rect = this.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const buffer = 120; // Account for sticky headers/nav
    
    // On mobile, we only auto-scroll if the section top is far out of view (e.g. user scrolled deep into list)
    // or if we are explicitly switching to map view.
    const shouldScrollPage = !isMobile || (rect.top < 0 || rect.top > buffer + 100);

    if (shouldScrollPage && (rect.top < buffer || rect.bottom > windowHeight)) {
      const scrollTarget = window.pageYOffset + rect.top - buffer;
      window.scrollTo({
        top: scrollTarget,
        behavior: 'smooth'
      });
    }

    // Handle map movement based on click source
    if (!fromMapClick) {
      // Clicked from list: pan ONLY if marker is outside viewport
      if (this.map && this.map.getBounds() && !this.map.getBounds().contains(partner.position)) {
        this.map.panTo(partner.position);
      }
      // No zooming requested
    }
    // If fromMapClick, do nothing to the viewport


    // 1. Reset previous marker if it exists
    if (this.activeMarkerIndex !== null && this.activeMarkerIndex !== index) {
      const prevPartner = this.partners.find(p => p.index === this.activeMarkerIndex);
      if (prevPartner && this.markers[this.activeMarkerIndex]) {
        this.markers[this.activeMarkerIndex].setIcon(this.createSvgMarkerIcon(prevPartner.category, false));
        this.markers[this.activeMarkerIndex].setZIndex(1);
      }
    }

    // 2. Highlight new marker
    if (this.markers[index]) {
      this.markers[index].setIcon(this.createSvgMarkerIcon(partner.category, true));
      this.markers[index].setZIndex(999);
    }
    
    this.activeMarkerIndex = index;

    const list = this.elements.partnerList;
    
    if (scrollList && list) {
      // 1. Fade out the list
      list.style.opacity = '0';
      
      // 2. Wait for fade transition
      setTimeout(() => {
        this.partners.forEach(p => p.element?.classList.remove('active'));
        partner.element.classList.add('active');

        // Scroll the list natively so the selected card comes into view gracefully
        // Use scrollTo on the list container instead of scrollIntoView to avoid scrolling the whole page
        // Change behavior to 'auto' (instant) so it happens while the list is faded out
        if (list && partner.element) {
          list.scrollTo({
            top: partner.element.offsetTop - 4, // Exactly at the top with small offset for outline
            behavior: 'auto'
          });
        }

        // 3. Fade back in
        list.style.opacity = '1';
      }, 300); // 300ms to match CSS transition
    } else {
      // Fallback if no scrolling requested or list missing
      this.partners.forEach(p => p.element?.classList.remove('active'));
      partner.element.classList.add('active');
    }

    this.updateMarkerCollisions();
  }

  updateMarkerCollisions() {
    if (!this.collisionOverlay || !this.collisionOverlay.getProjection()) return;
    const projection = this.collisionOverlay.getProjection();

    const activePool = this.partners.filter(p => this.markers[p.index] && this.markers[p.index]._matchesFilter);

    // Sort by priority so PROs and ACTIVE markers take precedence when hiding colliding ones
    activePool.sort((a, b) => {
       if (a.index === this.activeMarkerIndex) return -1;
       if (b.index === this.activeMarkerIndex) return 1;
       const rankA = a.category.includes('PRO') ? 3 : (a.category.includes('resel') ? 2 : 1);
       const rankB = b.category.includes('PRO') ? 3 : (b.category.includes('resel') ? 2 : 1);
       if (rankA !== rankB) return rankB - rankA;
       return 0;
    });

    const shown = [];
    const minDistance = 12; // Radius in pixels to consider "overlapping" (decreased to show more pins)

    for (const p of activePool) {
       const m = this.markers[p.index];
       const pos = projection.fromLatLngToDivPixel(p.position);
       if (!pos) continue;

       let overlaps = false;
       for (const s of shown) {
          const dx = pos.x - s.x;
          const dy = pos.y - s.y;
          if (Math.abs(dx) < minDistance && Math.abs(dy) < minDistance) {
            overlaps = true;
            break;
          }
       }

       // Always show the explicitly clicked active marker regardless of collisions
       if (overlaps && p.index !== this.activeMarkerIndex) {
          if (m.getMap()) m.setMap(null); 
       } else {
          if (!m.getMap()) m.setMap(this.map);
          shown.push(pos);
       }
    }
  }
}

customElements.define('partners-map', PartnersMap);
